/**
 * 模块: 配置结构总览组件
 * 职责: 以表格形式展示配置体系的完整结构
 * 限制: <200行
 */

import React from 'react';
import { Table, Alert, Tag, Progress, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { WeightSystemConfig } from '../../../services/configPresetAPI';
import {
  METRIC_DIMENSIONS,
  getComponentLabel,
  getComponentDescription,
  getMetricDimension,
  getIconComponent,
} from '../constants/componentLabels';

const { Text } = Typography;

// ============================================================================
// 类型定义
// ============================================================================

interface TableDataItem {
  key: string;
  category: string;
  categoryLabel: string;
  categoryDesc: string;
  categoryColor: string;
  component?: string;
  componentLabel?: string;
  weight?: number;
  description?: string;
  isCategory: boolean;
  children?: TableDataItem[];
}

interface ConfigStructureOverviewProps {
  config: WeightSystemConfig | null;
}

// ============================================================================
// 数据转换函数
// ============================================================================

const convertConfigToTableData = (config: WeightSystemConfig | null): TableDataItem[] => {
  if (!config) return [];

  const tableData: TableDataItem[] = [];

  // 遍历所有指标维度
  METRIC_DIMENSIONS.forEach((dimension) => {
    // 跳过市场矩阵（它的结构不同，不是基于权重的）
    if (dimension.key === 'market_matrix') {
      tableData.push({
        key: dimension.key,
        category: dimension.key,
        categoryLabel: dimension.label,
        categoryDesc: dimension.description,
        categoryColor: dimension.color,
        isCategory: true,
        description: '基于阈值的四象限分类，不使用权重计算',
      });
      return;
    }

    const metricConfig = config[dimension.key as keyof WeightSystemConfig];
    
    // 检查是否有components
    if (!metricConfig || typeof metricConfig !== 'object' || !('components' in metricConfig)) {
      return;
    }

    const components = (metricConfig as any).components || [];
    const children: TableDataItem[] = components.map((comp: any) => ({
      key: `${dimension.key}.${comp.name}`,
      category: dimension.key,
      categoryLabel: dimension.label,
      categoryDesc: dimension.description,
      categoryColor: dimension.color,
      component: comp.name,
      componentLabel: getComponentLabel(dimension.key, comp.name),
      weight: comp.weight,
      description: getComponentDescription(dimension.key, comp.name),
      isCategory: false,
    }));

    // 计算总权重
    const totalWeight = components.reduce((sum: number, comp: any) => sum + comp.weight, 0);

    tableData.push({
      key: dimension.key,
      category: dimension.key,
      categoryLabel: dimension.label,
      categoryDesc: dimension.description,
      categoryColor: dimension.color,
      isCategory: true,
      weight: totalWeight,
      description: `${dimension.description} (${components.length}个子维度)`,
      children,
    });
  });

  return tableData;
};

// ============================================================================
// 主组件
// ============================================================================

export const ConfigStructureOverview: React.FC<ConfigStructureOverviewProps> = ({ config }) => {
  const tableData = convertConfigToTableData(config);

  // ============================================================================
  // 表格列定义
  // ============================================================================

  const columns: ColumnsType<TableDataItem> = [
    {
      title: '分类/子维度',
      dataIndex: 'categoryLabel',
      key: 'name',
      width: '25%',
      render: (_, record) => {
        if (record.isCategory) {
          const dimension = getMetricDimension(record.category);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {dimension && getIconComponent(dimension.iconType)}
              <Text strong style={{ fontSize: 15, color: record.categoryColor }}>
                {record.categoryLabel}
              </Text>
            </div>
          );
        }
        return (
          <div style={{ paddingLeft: 32 }}>
            <Text>{record.componentLabel}</Text>
          </div>
        );
      },
    },
    {
      title: '权重',
      dataIndex: 'weight',
      key: 'weight',
      width: '25%',
      align: 'center',
      render: (weight, record) => {
        if (record.category === 'market_matrix') {
          return <Text type="secondary">-</Text>;
        }

        if (!weight && weight !== 0) {
          return <Text type="secondary">-</Text>;
        }

        if (record.isCategory) {
          const isBalanced = weight === 100;
          return (
            <Tag color={isBalanced ? 'success' : 'warning'} style={{ fontSize: 14 }}>
              总计: {weight}%
            </Tag>
          );
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Progress
              percent={weight}
              size="small"
              strokeColor={record.categoryColor}
              style={{ width: 80 }}
            />
            <Tag color={record.categoryColor}>{weight}%</Tag>
          </div>
        );
      },
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
      width: '50%',
      render: (description, record) => {
        if (record.isCategory) {
          return <Text type="secondary">{description}</Text>;
        }
        return <Text style={{ fontSize: 13 }}>{description}</Text>;
      },
    },
  ];

  // ============================================================================
  // 渲染
  // ============================================================================

  return (
    <div className="config-structure-overview">
      <Alert
        message="配置结构说明"
        description="此表格展示了当前配置中各个分类指标的组成结构。每个分类由多个子维度组成，通过调整子维度的权重来改变该分类的计算侧重点。您可以在各分类的Tab中进行权重调整。"
        type="info"
        showIcon
        closable
        style={{ marginBottom: 16 }}
      />

      <Table
        className="structure-table"
        columns={columns}
        dataSource={tableData}
        pagination={false}
        defaultExpandAllRows={true}
        rowClassName={(record) => (record.isCategory ? 'category-row' : 'component-row')}
        bordered
        size="middle"
      />
    </div>
  );
};

